import type { ReactNode } from "react";
import type { HeatLossAfterPlanEdit } from "../api/types.js";

/**
 * What to say right after a laser calibration or a plan correction, about the
 * heat loss — shown once, beside the change that caused it.
 *
 * The rooms follow the scan (apps/api/src/survey/scan-merge.ts): the scan's
 * own figures are brought up to date and what a person entered or confirmed
 * is kept. Nothing needs saying when nobody's work was involved — the heat
 * loss simply changes. When something of theirs was kept the API sends one
 * sentence saying what was updated and what was kept, and each thing the
 * designer should check (a kept value the new geometry disagrees with, a
 * wall kept though no longer on the plan) in plain English. When the rooms
 * could not follow at all (a completed survey), the sentence says why.
 */
export interface HeatLossWords {
  readonly note: string;
  readonly reviewNotes: readonly string[];
}

export function heatLossWordsOf(outcome: HeatLossAfterPlanEdit | undefined): HeatLossWords | null {
  if (!outcome) return null;
  if (outcome.status === "kept") return { note: outcome.note, reviewNotes: [] };
  if (outcome.status === "recalculated" && outcome.note !== undefined) {
    return { note: outcome.note, reviewNotes: outcome.reviewNotes ?? [] };
  }
  return null;
}

/** The things to check, one per line — nothing when there are none. */
export function ReviewNoteList({
  notes,
  className,
  testId,
}: {
  readonly notes: readonly string[];
  readonly className: string;
  readonly testId: string;
}): ReactNode {
  if (notes.length === 0) return null;
  return (
    <ul className={className} data-testid={testId}>
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}
